import { ButtonGroup } from "@/components/ButtonGroup";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PlayerContentSelect } from "./PlayerContentSelect";
import { PlayerContentGroupSelect } from "./PlayerContentGroupSelect";
import { PlayerChannelSelect } from "./PlayerChannelSelect";
import { FormLabel, FormMessage } from "@/components/ui/form";
import { SelectedMediaPreview } from "@/pages/shared/SelectedMediaPreview";
import { XIcon } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ContentGroup } from "@/lib/types";

export type AssignableMediaTypes = "channel" | "contentGroup" | "content";

interface MediaValue {
  channelId?: number;
  contentGroupId?: number;
  contentId?: number;
}

interface PlayerMediaSelectRawProps {
  value: MediaValue;
  onChange: (value: MediaValue, selectedType: AssignableMediaTypes) => void;
  visibleTypes: AssignableMediaTypes[];
  label?: string;
  onClear: () => void;
  enabledTypes?: AssignableMediaTypes[];
  defaultScreenSizeGuid?: string;
  customFilter?: (item: ContentGroup) => boolean;
}

export const PlayerMediaSelectRaw: React.FC<PlayerMediaSelectRawProps> = ({
  value,
  onChange,
  visibleTypes,
  label,
  onClear,
  enabledTypes,
  defaultScreenSizeGuid,
  customFilter,
}) => {
  const { t } = useTranslation();
  const [selectedMediaType, setSelectedMediaType] = useState<AssignableMediaTypes>();
  const [showChannelSelect, setShowChannelSelect] = useState(false);
  const [showContentGroupSelect, setShowContentGroupSelect] = useState(false);
  const [showContentSelect, setShowContentSelect] = useState(false);

  // Set initial media type based on value
  useEffect(() => {
    if (value.channelId) {
      setSelectedMediaType("channel");
    } else if (value.contentGroupId) {
      setSelectedMediaType("contentGroup");
    } else if (value.contentId) {
      setSelectedMediaType("content");
    }
  }, [JSON.stringify(value)]);

  const buttons: React.ComponentProps<typeof ButtonGroup>["items"] = visibleTypes.map((type) => ({
    id: type,
    text: t(`entities.${type}`),
    disabled: !enabledTypes?.includes(type),
  }));

  if (selectedMediaType || 1) {
    buttons.push({
      id: "clear" as any,
      icon: (
        <Tooltip>
          <TooltipTrigger>
            <XIcon className="w-4 h-4" />
          </TooltipTrigger>
          <TooltipContent>{t("common.reset")}</TooltipContent>
        </Tooltip>
      ),
    });
  }

  const handleValueChange = (type: AssignableMediaTypes, id?: number) => {
    const newValue: MediaValue = {
      channelId: undefined,
      contentGroupId: undefined,
      contentId: undefined,
    };

    if (id) {
      switch (type) {
        case "channel":
          newValue.channelId = id;
          break;
        case "contentGroup":
          newValue.contentGroupId = id;
          break;
        case "content":
          newValue.contentId = id;
          break;
      }
    }

    onChange(newValue, type);
  };

  return (
    <div>
      <FormLabel>{label || t("common.media")}</FormLabel>
      <div className="mt-3">
        <ButtonGroup
          items={buttons}
          selectedId={selectedMediaType}
          onClick={(id) => {
            setShowChannelSelect(id === "channel");
            setShowContentGroupSelect(id === "contentGroup");
            setShowContentSelect(id === "content");
            if (id === "clear") {
              onClear();
              setSelectedMediaType(undefined);
            }
          }}
        />
      </div>
      {showChannelSelect && (
        <PlayerChannelSelect
          value={value.channelId}
          onChange={(id) => {
            handleValueChange("channel", id);
            setShowChannelSelect(false);
          }}
          onOpenChange={() => setShowChannelSelect(false)}
          defaultScreenSizeGuid={defaultScreenSizeGuid}
        />
      )}
      {showContentGroupSelect && (
        <PlayerContentGroupSelect
          value={value.contentGroupId}
          onChange={(id) => {
            handleValueChange("contentGroup", id);
            setShowContentGroupSelect(false);
          }}
          onOpenChange={() => setShowContentGroupSelect(false)}
          defaultScreenSizeGuid={defaultScreenSizeGuid}
          customFilter={customFilter}
        />
      )}
      {showContentSelect && (
        <PlayerContentSelect
          onChange={(id) => {
            handleValueChange("content", id);
            setShowContentSelect(false);
          }}
          onOpenChange={() => setShowContentSelect(false)}
          defaultScreenSizeGuid={defaultScreenSizeGuid}
        />
      )}
      <SelectedMediaPreview
        contentIntId={value.contentId}
        contentGroupIntId={value.contentGroupId}
        channelIntId={value.channelId}
        showInlineContentPreview
      />
      <FormMessage />
    </div>
  );
};
